import {
  Box,
  Divider,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';
import { FunctionComponent, ReactNode, SyntheticEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AutocompleteOptions } from '@Components/Autocomplete';
import Button from '@Components/Button';
import Chip from '@Components/Chip';
import DateRangePicker from '@Components/DateRangePicker';
import Radio from '@Components/Radio';
import VirtualizedAutocomplete from '@Components/VirtualizedAutocomplete';
import { ReportTypes, VoiceProviderTypes } from '@Typings/global.types';

import ReportFilters from './ReportConfig.constants';
import {
  FilterContainer,
  FlexCenterBox,
  StyledRadioGroup,
} from './ReportConfig.styles';
import {
  ReportConfigProps,
  ReportData,
  ReportFilterKey,
} from './ReportConfig.types';

const ReportConfig: FunctionComponent<ReportConfigProps> = (
  props: ReportConfigProps
): JSX.Element => {
  const { t } = useTranslation();
  const {
    filterState,
    setFilterState,
    initialReportType,
    type,
    primaryBtnProps,
    secondaryBtnProps,
    sessionAutocompleteProps,
    accountAutocompleteProps,
    dateRangeProps,
    voiceProviderProps,
    setVoiceProviderProps,
  } = props;

  const {
    sessionAutocompleteValue,
    setSessionAutocompleteValue,
    ...resSessionAutocompleteProps
  } = sessionAutocompleteProps;

  /** Report Filters */
  const reportFilters = ReportFilters();

  /** STATES */
  const [reportType, setReportType] = useState<ReportTypes>(
    initialReportType || ReportTypes.CallLog
  );

  /** Check if Selected Filter is in Filter State */
  const isSelected = (key: ReportFilterKey, id: string): boolean =>
    filterState[key].indexOf(id) !== -1;

  /** Handles Filter Click (toggles filter chip button)
   * To know which filters chip are toggled
   */
  const handleFilterChipClick = (key: ReportFilterKey, id: string): void => {
    const index = filterState[key].indexOf(id);
    let newState: string[] = [];

    if (index === -1) {
      /** Adds id to filterState array state with the given key if not present */
      newState = newState.concat(filterState[key], id);
    } else if (index === 0) {
      /** Edge Case - Removes the id if already present in filterState array with the given key at starting index */
      newState = newState.concat(filterState[key].slice(1));
    } else if (index === filterState[key].length - 1) {
      /** Edge Case - Removes the id if already present in filterState array with the given key at last index */
      newState = newState.concat(filterState[key].slice(0, -1));
    } else if (index > 0) {
      /** Removes the id if already present in filterState array with the given key at any other index */
      newState = newState.concat(
        filterState[key].slice(0, index),
        filterState[key].slice(index + 1)
      );
    }

    setFilterState((prevState) => ({ ...prevState, [key]: newState }));
  };

  /** Handles Report Radio Button Group Change */
  const handleReportChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setReportType((event.target as HTMLInputElement).value as ReportTypes);
  };

  /** Handles Voice Provider Radio Button Group Change */
  const handleVoiceProviderChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (setVoiceProviderProps) {
      setVoiceProviderProps(
        (event.target as HTMLInputElement).value as VoiceProviderTypes
      );
    }
  };

  /** Handles Autocomplete Value Change */
  const handleAutocompleteOnChange = (
    _event: SyntheticEvent<Element, Event>,
    newValue: AutocompleteOptions[],
    isSession?: boolean
  ) =>
    isSession
      ? setSessionAutocompleteValue(newValue)
      : accountAutocompleteProps?.setAccountAutocompleteValue(newValue);

  /** Handles Accept Btn Click
   * AcceptBtn Function param -> filters selected
   */
  const handlePrimaryBtnClick = () => {
    if (primaryBtnProps && primaryBtnProps.onClick) {
      const data: ReportData = {
        type: reportType,
        accounts: accountAutocompleteProps?.accountAutocompleteValue.map(
          (item) => item.value
        ),
        sessions: sessionAutocompleteValue.map((item) => item.value),
        voiceProvider: voiceProviderProps || VoiceProviderTypes.Both,
        filters: {
          status: filterState.status,
          disposition: filterState.disposition,
          callStatus: filterState.callStatus,
        },
        startDate: dateRangeProps.dateState[0].startDate,
        endDate: dateRangeProps.dateState[0].endDate,
      };

      primaryBtnProps.onClick(data);
    }
  };

  const handleSecondaryBtnClick = () => {
    setReportType(ReportTypes.CallLog);
    if (secondaryBtnProps && secondaryBtnProps.onClick) {
      secondaryBtnProps.onClick();
    }
  };

  const reportTypeFilter = () => (
    <FilterContainer
      mb={2.4}
      sx={{
        p: type === 'modal' ? 0 : undefined,
        justifyContent: 'space-between',
      }}
    >
      <Box
        minWidth={type === 'report' ? '50%' : undefined}
        display="flex"
        alignItems="flex-start"
      >
        <Typography
          variant="input"
          fontWeight="bold"
          sx={{
            minWidth: '11.8rem',
            color: type === 'modal' ? 'text.disabled' : 'text.primary',
          }}
        >
          {`${t('reports.Type of Report')}:`}
        </Typography>
        <StyledRadioGroup
          row
          aria-label="report-type"
          name="report-type-radio-group"
          value={reportType}
          onChange={handleReportChange}
        >
          <FormControlLabel
            value={ReportTypes.CallLog}
            control={<Radio />}
            label={t('reports.Call Log')}
            disabled={type === 'modal'}
          />
          <FormControlLabel
            value={ReportTypes.Sessions}
            control={<Radio />}
            label={t('reports.Sessions Summary')}
            disabled={type === 'modal'}
          />
          {accountAutocompleteProps && (
            <FormControlLabel
              value={ReportTypes.ActiveSessions}
              control={<Radio />}
              label={t('reports.Active Sessions')}
              disabled={type === 'modal'}
            />
          )}
          <FormControlLabel
            value={ReportTypes.EmailLog}
            control={<Radio />}
            label={t('reports.Email Log')}
            disabled={type === 'modal'}
          />
        </StyledRadioGroup>
      </Box>
      <FlexCenterBox minWidth={type === 'report' ? '50%' : undefined}>
        <Typography
          variant="input"
          fontWeight="bold"
          sx={{
            minWidth: '9.4rem',
            ml: type === 'modal' ? 0 : undefined,
            mx: 1.6,
          }}
        >
          {`${t('reports.Date Range')}:`}
        </Typography>
        <Stack minWidth="25.6rem">
          <DateRangePicker {...dateRangeProps} />
        </Stack>
      </FlexCenterBox>
    </FilterContainer>
  );

  /** Returns Account Name Autocomplete Filter */
  const accountAutocompleteFilter = (): JSX.Element | null => {
    if (accountAutocompleteProps) {
      const {
        accountAutocompleteValue,
        setAccountAutocompleteValue,
        ...resAccountAutocompleteProps
      } = accountAutocompleteProps;

      return (
        <Box>
          <Typography variant="input" fontWeight="bold">
            {`${t('reports.account')}:`}
          </Typography>
          <VirtualizedAutocomplete
            {...resAccountAutocompleteProps}
            id="account-dropdown"
            value={accountAutocompleteValue}
            onChange={(
              e: SyntheticEvent<Element, Event>,
              value: AutocompleteOptions[]
            ) => handleAutocompleteOnChange(e, value)}
            sx={{
              minWidth: type === 'modal' ? '54rem' : '46.4rem',
              maxWidth: type === 'modal' ? '54rem' : '46.4rem',
              mb: 3.2,
            }}
            textFieldProps={{
              ...resAccountAutocompleteProps.textFieldProps,
              placeholder: t('reports.Select Account Name'),
            }}
            noOptionsText={t('reports.No account available')}
          />
        </Box>
      );
    }

    return null;
  };

  const providerFilter = (): JSX.Element | null => (
    <Box
      minWidth={type === 'report' ? '50%' : undefined}
      display="flex"
      alignItems="flex-start"
      ml={1.6}
    >
      <Typography
        variant="input"
        fontWeight="bold"
        sx={{
          minWidth: '11.8rem',
          color: 'text.primary',
        }}
      >
        {`${t('reports.Type of Voice Provider')}:`}
      </Typography>
      <StyledRadioGroup
        row
        aria-label="voice-provider-type"
        name="voice-provider-type-radio-group"
        value={voiceProviderProps}
        onChange={handleVoiceProviderChange}
      >
        <FormControlLabel
          value={VoiceProviderTypes.Both}
          control={<Radio />}
          label={t('reports.All')}
          disabled={reportType === ReportTypes.EmailLog}
        />
        <FormControlLabel
          value={VoiceProviderTypes.Vonage}
          control={<Radio />}
          label={t('reports.Vonage')}
          disabled={reportType === ReportTypes.EmailLog}
        />
        <FormControlLabel
          value={VoiceProviderTypes.Twilio}
          control={<Radio />}
          label={t('reports.Twilio')}
          disabled={reportType === ReportTypes.EmailLog}
        />
        <FormControlLabel
          value={VoiceProviderTypes.Telnyx}
          control={<Radio />}
          label={t('reports.Telnyx')}
          disabled={reportType === ReportTypes.EmailLog}
        />
      </StyledRadioGroup>
    </Box>
  );

  const sessionFilter = () => (
    <FilterContainer
      flexDirection={type === 'report' ? 'row' : 'column'}
      sx={{ p: type === 'modal' ? 0 : undefined }}
    >
      <Stack
        flexDirection={type === 'modal' ? 'row' : 'column'}
        justifyContent={type === 'modal' ? 'space-between' : 'flex-start'}
        alignItems={type === 'report' ? 'flex-start' : undefined}
        flexWrap="wrap"
        flex="0 0 50%"
      >
        {accountAutocompleteFilter()}
        <Box mb={3.2}>
          <Typography variant="input" fontWeight="bold">
            {`${t('reports.Session Name')}:`}
          </Typography>
          <VirtualizedAutocomplete
            {...resSessionAutocompleteProps}
            id="session-dropdown"
            value={sessionAutocompleteValue}
            onChange={(
              e: SyntheticEvent<Element, Event>,
              value: AutocompleteOptions[]
            ) => handleAutocompleteOnChange(e, value, true)}
            sx={{
              minWidth: type === 'modal' ? '54rem' : '46.4rem',
              maxWidth: type === 'modal' ? '54rem' : '46.4rem',
            }}
            textFieldProps={{
              ...resSessionAutocompleteProps.textFieldProps,
              placeholder: t('reports.Select Session Name'),
            }}
            disabled={reportType === ReportTypes.EmailLog}
          />
        </Box>
      </Stack>
      <Stack
        flex="0 0 50%"
        divider={
          <Divider
            component="div"
            orientation="horizontal"
            sx={{
              my: 2.4,
              borderColor: ({ colors }) => colors.transparent12,
            }}
          />
        }
      >
        {accountAutocompleteProps && providerFilter()}
        {Object.keys(reportFilters).map(
          (key: string, index: number): ReactNode => {
            const reportFilterKey = key as ReportFilterKey;

            return (
              <Stack key={index} direction="row" ml={1.6}>
                <Typography
                  variant="input"
                  fontWeight="bold"
                  sx={{
                    mr: 1,
                    py: 0.8,
                    minWidth: '14rem',
                    maxHeight: '3.2rem',
                  }}
                >
                  {`${t(`reports.${reportFilterKey}`)}:`}
                </Typography>
                <Stack
                  direction="row"
                  gap={1.6}
                  flexWrap="wrap"
                  data-testid={key}
                >
                  {reportFilters[reportFilterKey].map(
                    (filter: AutocompleteOptions, idx: number): ReactNode => {
                      const { label, value } = filter;
                      const selectedState = isSelected(reportFilterKey, value);
                      return (
                        <Chip
                          key={idx}
                          value={value}
                          selected={selectedState}
                          onClick={() =>
                            handleFilterChipClick(reportFilterKey, value)
                          }
                          disabled={reportType === ReportTypes.EmailLog}
                        >
                          {label}
                        </Chip>
                      );
                    }
                  )}
                </Stack>
              </Stack>
            );
          }
        )}
      </Stack>
    </FilterContainer>
  );

  const actionsBtn = () => (
    <Stack direction="row" mt={type === 'report' ? 2.4 : 3.2}>
      <Button
        variant="text"
        color="secondary"
        sx={{ ml: 'auto', mr: 1.6 }}
        {...secondaryBtnProps}
        onClick={handleSecondaryBtnClick}
      />
      <Button
        color="primary"
        {...primaryBtnProps}
        onClick={handlePrimaryBtnClick}
      />
    </Stack>
  );

  return (
    <Stack>
      {reportTypeFilter()}
      {sessionFilter()}
      {actionsBtn()}
    </Stack>
  );
};

export default ReportConfig;
